import type { BusinessCalendar, DateOnly } from '@/domain/dates'
import { workingDaysInYear } from './calendar'
import type { Balance, BalanceRecord, Employee, LeaveRequest, OrgSettings } from './types'

const ACTIVE = new Set(['approved', 'pending'])

export function carryOverDeadline(settings: OrgSettings, year: number): DateOnly {
  return `${year}-${settings.carryOverDeadline}` as DateOnly
}

/**
 * Vacation balance for one person and year (port of the Apps Script `_calcularSaldo`).
 *
 * - Contract days: the yearly record, else the person's override, else the organization default.
 * - Carried-over days are valid until the deadline (inclusive, Lisbon civil date).
 * - Approved days consume carried-over days first (FIFO), then contract days.
 * - Pending days are covered by what is left of the carried-over days, then by contract days.
 *
 * Fix vs. the legacy version: carried-over days that cover a pending request are no longer
 * counted again as "available" (the old total could exceed the real balance).
 */
export function computeBalance(args: {
  employee: Pick<Employee, 'id' | 'annualDays'>
  year: number
  requests: LeaveRequest[]
  record: BalanceRecord | undefined
  settings: OrgSettings
  calendar: BusinessCalendar
  today: DateOnly
}): Balance {
  const { employee, year, requests, record, settings, calendar, today } = args
  const contractDays = record?.contractDays ?? employee.annualDays ?? settings.annualDays
  const deadline = carryOverDeadline(settings, year)
  const carryOverOriginal = settings.carryOverEnabled ? (record?.carriedOver ?? 0) : 0
  const active = carryOverOriginal > 0 && today <= deadline
  const carryOver = active ? carryOverOriginal : 0

  let used = 0
  let pending = 0
  for (const r of requests) {
    if (r.employeeId !== employee.id || r.type !== 'vacation' || !ACTIVE.has(r.status)) continue
    const d = workingDaysInYear(calendar, r.start, r.end, year)
    if (r.status === 'approved') used += d
    else pending += d
  }

  const carryOverRef = active ? carryOver : carryOverOriginal
  const carryOverUsed = Math.min(carryOverRef, used)
  const usedContract = Math.max(0, used - carryOverRef)
  const carryAfterApproved = Math.max(0, carryOver - carryOverUsed)
  const pendingOnCarry = active ? Math.min(carryAfterApproved, pending) : 0
  const pendingContract = Math.max(0, pending - pendingOnCarry)
  const remaining = Math.max(0, contractDays - usedContract - pendingContract)
  const carryOverLeft = Math.max(0, carryAfterApproved - pendingOnCarry)

  return {
    year,
    contractDays,
    carryOver,
    carryOverOriginal,
    carryOverUsed,
    carryOverLeft,
    carryOverDeadline: deadline,
    carryOverExpired: carryOverOriginal > 0 && !active,
    used: usedContract,
    pending: pendingContract,
    remaining,
    available: remaining + carryOverLeft,
  }
}

/** Days planned (approved + pending) out of everything the person can take this year. */
export function planningProgress(b: Balance): { planned: number; total: number } {
  // Carried-over days already used still count, even after their deadline has passed.
  const total = b.contractDays + Math.max(b.carryOver, b.carryOverUsed)
  return { planned: Math.max(0, total - b.available), total }
}

export interface LegalRuleStatus {
  required: number
  /** Longest single vacation period (approved or pending) in working days. */
  longest: number
  met: boolean
  /** Working days still missing in one period. */
  missing: number
}

/** Código do Trabalho: one period of at least N consecutive working days per year. */
export function legalRuleStatus(requests: LeaveRequest[], employeeId: string, year: number, required: number): LegalRuleStatus {
  let longest = 0
  for (const r of requests) {
    if (r.employeeId !== employeeId || r.type !== 'vacation' || !ACTIVE.has(r.status)) continue
    if (!r.start.startsWith(`${year}-`) && !r.end.startsWith(`${year}-`)) continue
    longest = Math.max(longest, r.workingDays)
  }
  return { required, longest, met: longest >= required, missing: Math.max(0, required - longest) }
}
